<template>
  <div class="modal-overlay planning">
    <div class="modal-content">
      <div class="modal-header">
        <h3><span v-if="searchLoading">Recherche en cours...</span><span v-else>Modifier le planning</span></h3>
        <button class="close-btn" @click="close">&times;</button>
      </div>

      <div class="modal-body">
        <div v-if="error" class="alert alert-danger">
          {{ error }}
        </div>


        <form @submit.prevent="submitForm">

          <div v-if="hasMultipleOfs" class="form-group">
            <div class="form-check">
              <input
                  id="toggleLinkedOf"
                  v-model="isLinkOtherOfEnabled"
                  class="form-check-input"
                  type="checkbox"
              />
              <label class="form-check-label form-label" for="toggleLinkedOf">Gérer les candidatures des centres de :</label>
            </div>

            <div v-if="isLinkOtherOfEnabled">
              <div v-if="selectedOfObjects.length > 0" class="pills-container">
                <span
                    v-for="of in selectedOfObjects"
                    :key="of.ofObjId"
                    class="pill"
                >
                  {{ of.ofName }}
                  <button
                      v-if="isRemovable(of.ofObjId)"
                      type="button"
                      class="pill-remove"
                      @click="removeOf(of.ofObjId)"
                  >&times;</button>
                </span>
              </div>

              <select
                  id="linkedOf"
                  v-model="selectedOf"
                  class="form-control"
                  @change="addOf"
              >
                <option
                    v-for="of in filteredOfList"
                    :key="of.ofObjId"
                    :value="of.ofObjId"
                >
                  {{ of.ofName }}
                </option>
              </select>
            </div>
          </div>

          <div class="form-group">
            <SearchInput
                v-model="searchTerm"
                :clear-button-title="UI_MESSAGES.CLEAR_BUTTON_TITLE"
                :has-value="!!(formData.interviewManagerObjId && formData.interviewManagerObjId > 0)"
                :loading="searchLoading"
                :help-text="UI_MESSAGES.HELP_TEXT_LEAVE_EMPTY"
                :label="'Entretiens réalisés par :'"
                :placeholder="(formData.interviewManagerObjId && formData.interviewManagerObjId > 0) ? UI_MESSAGES.SEARCH_MANAGER_PLACEHOLDER_MODIFY : UI_MESSAGES.SEARCH_MANAGER_PLACEHOLDER"
                :suggestion-key-field="'interviewManagerObjId'"
                :suggestions="suggestions"
                @clear="clearSelection"
                @search="onSearch"
                @select="selectInterviewManager"
                @clear-suggestions="clearSuggestions"
            >
              <template #suggestion="{ suggestion }">
                {{ formatPersonDisplayName(suggestion) }}
                <small v-if="suggestion.email"> — {{ suggestion.email }}</small>
              </template>
            </SearchInput>
          </div>

          <div class="form-group">
            <label class="form-label">Suivre par email l'avancement des convocations :</label>
            <div class="form-check">
              <input
                  id="notificationOwner"
                  v-model="formData.notificationOwner"
                  class="form-check-input"
                  type="checkbox"
              />
              <label class="form-label form-check-label" for="notificationOwner">{{ planning.owner.firstname }} {{ planning.owner.lastname }}<small>— {{ planning.owner.email }}</small></label>
            </div>
            <div v-if="selectedInterviewManagerName" class="form-check">
              <input
                  id="notificationInterviewManager"
                  v-model="formData.notificationInterviewManager"
                  class="form-check-input"
                  type="checkbox"
              />
              <label class="form-label form-check-label" for="notificationInterviewManager">{{ selectedInterviewManagerName }}<small v-if="selectedInterviewManagerEmail">— {{ selectedInterviewManagerEmail }}</small></label>
            </div>
          </div>

          <div class="planning-info">
            <p><strong>Début:</strong> {{ formatDateTime(planning.startAt) }}</p>
            <p><strong>Fin:</strong> {{ formatDateTime(planning.endAt) }}</p>
            <p><strong>Entretiens réalisés par :</strong> {{ formatPersonDisplayName(planning.interviewManager) }}</p>
          </div>

          <div class="form-actions">
            <button
                :disabled="loading"
                class="btn btn-primary"
                type="submit"
            >
              {{ loading ? 'Mise à jour...' : 'Mettre à jour' }}
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script setup>
import {computed, onMounted, reactive, ref, watch} from 'vue'
import {useRoute} from 'vue-router'
import {usePlanningStore} from '@/store/planningStore'
import {useDateFormatter} from '@/composables/useDateFormatter'
import {formatPersonDisplayName, useSearchAutocomplete} from '@/composables/useSearchAutocomplete'
import SearchInput from '@/components/UI/SearchInput.vue'
import {SEARCH_CONFIG, UI_MESSAGES} from '@/constants/planning'

const emit = defineEmits(['close', 'updated'])

const props = defineProps({
  planning: {
    type: Object,
    required: true
  }
})

const route = useRoute()
const planningStore = usePlanningStore()
const {formatDateTime} = useDateFormatter()
const loading = ref(false)
const error = ref(null)

const formData = reactive({
  interviewManagerObjId: props.planning.interviewManagerObjId,
  ofsIdsEffective: props.planning.ofsIdsEffective ? [...props.planning.ofsIdsEffective] : [],
  notificationOwner: props.planning.notificationOwner || false,
  notificationInterviewManager: props.planning.notificationInterviewManager || false
})

const selectedInterviewManagerName = ref(
  props.planning.interviewManager ? formatPersonDisplayName(props.planning.interviewManager) : null
)

const selectedInterviewManagerEmail = ref(
  (props.planning.interviewManager && props.planning.interviewManager.email)
    ? props.planning.interviewManager.email
    : null
)

const selectedOf = ref('')
const isLinkOtherOfEnabled = ref(false)

const ofList = computed(() => planningStore.ofList)

const hasMultipleOfs = computed(() => ofList.value.length > 1)

const filteredOfList = computed(() => {
  return ofList.value.filter(of => {
    const alreadyInPlanning = props.planning.ofsIdsEffective && props.planning.ofsIdsEffective.includes(of.ofObjId)
    return !alreadyInPlanning && !formData.ofsIdsEffective.includes(of.ofObjId)
  })
})

const selectedOfObjects = computed(() => {
  return ofList.value.filter(of => formData.ofsIdsEffective.includes(of.ofObjId))
})

const setLinkToggleFromData = () => {
  if (formData.ofsIdsEffective.length > 0) {
    isLinkOtherOfEnabled.value = true
  }
}

onMounted(() => {
  planningStore.fetchOfList(route.params.ofObjId, route.params.userAccountObjId).then(() => {
    setLinkToggleFromData()
  })
})

watch(hasMultipleOfs, (value) => {
  if (value && formData.ofsIdsEffective.length > 0) {
    isLinkOtherOfEnabled.value = true
  }
})

watch(() => formData.ofsIdsEffective.length, (length) => {
  if (length > 0) {
    isLinkOtherOfEnabled.value = true
  }
})

watch(isLinkOtherOfEnabled, (enabled) => {
  if (!enabled && formData.ofsIdsEffective.length > 0) {
    isLinkOtherOfEnabled.value = true
  }
})


const addOf = () => {
  if (selectedOf.value && !formData.ofsIdsEffective.includes(selectedOf.value)) {
    formData.ofsIdsEffective.push(selectedOf.value)
    selectedOf.value = ''
  }
}

const isRemovable = (ofObjId) => {
  return !(props.planning.ofsIdsEffective && props.planning.ofsIdsEffective.includes(ofObjId))
}

const removeOf = (ofObjId) => {
  if (!isRemovable(ofObjId)) return
  formData.ofsIdsEffective = formData.ofsIdsEffective.filter(id => id !== ofObjId)
  clearSelection()
}

// Recherche avec autocomplete - doit être défini avant le watch qui l'utilise
const searchFunction = async (term) => {

  const promises = []


  // Recherche pour les centres liés
  if (formData.ofsIdsEffective && formData.ofsIdsEffective.length !== 0) {
    formData.ofsIdsEffective.forEach(id => {
      promises.push(
          planningStore.searchInterviewManagers(
              id,
              route.params.userAccountObjId,
              term
          )
      )
    })
  } else {
    // Recherche pour le centre principal
    promises.push(
        planningStore.searchInterviewManagers(
            route.params.ofObjId,
            route.params.userAccountObjId,
            term
        )
    )
  }

  const results = await Promise.all(promises)


  const allManagers = results.map(res => {
    if (Array.isArray(res)) {
      return res
    }
    return []
  }).flat()

  const uniqueManagers = []
  const seenIds = new Set()

  for (const manager of allManagers) {
    const id = manager.interviewManagerObjId || manager.id
    if (id && !seenIds.has(id)) {
      seenIds.add(id)
      uniqueManagers.push(manager)
    }
  }

  return uniqueManagers
}

const {
  searchTerm,
  suggestions,
  searchLoading,
  onSearch,
  clearSuggestions,
  clearSearch,
  selectSuggestion
} = useSearchAutocomplete(searchFunction, SEARCH_CONFIG.DEBOUNCE_MS, SEARCH_CONFIG.MIN_LENGTH)

// Maintenant on peut utiliser searchTerm dans le watch
watch(() => props.planning, (newPlanning) => {
  formData.interviewManagerObjId = newPlanning.interviewManagerObjId
  formData.ofsIdsEffective = newPlanning.ofsIdsEffective ? [...newPlanning.ofsIdsEffective] : []
  formData.notificationOwner = newPlanning.notificationOwner || false
  formData.notificationInterviewManager = newPlanning.notificationInterviewManager || false
  selectedInterviewManagerName.value = (newPlanning.interviewManager && newPlanning.interviewManagerObjId > 0)
    ? formatPersonDisplayName(newPlanning.interviewManager)
    : null
  selectedInterviewManagerEmail.value = (newPlanning.interviewManager && newPlanning.interviewManager.email)
    ? newPlanning.interviewManager.email
    : null
  setLinkToggleFromData()
  if (newPlanning.interviewManager && newPlanning.interviewManagerObjId > 0) {
    searchTerm.value = formatPersonDisplayName(newPlanning.interviewManager)
  }
}, {immediate: true})


const close = () => {
  emit('close')
}

const submitForm = async () => {
  loading.value = true
  error.value = null

  const updateData = {}


  updateData.interviewManagerObjId = formData.interviewManagerObjId
  updateData.notificationOwner = formData.notificationOwner
  updateData.notificationInterviewManager = formData.notificationInterviewManager


  const initialOfs = props.planning.ofsIdsEffective || []
  const hasOfChanges = formData.ofsIdsEffective.length !== initialOfs.length || formData.ofsIdsEffective.some(id => !initialOfs.includes(id))

  if (hasOfChanges) {
    updateData.ofsIdsEffective = formData.ofsIdsEffective
  }

  planningStore.updatePlanning(route.params.ofObjId, route.params.userAccountObjId, props.planning.id, updateData).then(() => {
    emit('updated')
    close()
  }).catch((err) => {
    error.value = err.message || err || 'Erreur lors de la mise à jour du planning'
  }).finally(() => {
    loading.value = false
  })
}

const selectInterviewManager = (manager) => {
  const selected = selectSuggestion(manager)
  formData.interviewManagerObjId = selected.interviewManagerObjId
  selectedInterviewManagerName.value = formatPersonDisplayName(selected)
  selectedInterviewManagerEmail.value = selected.email || null
}

const clearSelection = () => {
  clearSearch()
  formData.interviewManagerObjId = null
  selectedInterviewManagerName.value = null
  selectedInterviewManagerEmail.value = null
}
</script>

<style scoped>
/* Styles spécifiques au composant - la plupart des styles sont maintenant dans global.css */
.planning .modal-content {
  overflow-y: visible;
}

.pills-container {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 10px;
}

.pill {
  display: inline-flex;
  align-items: center;
  background-color: #e9ecef;
  color: #495057;
  padding: 4px 12px;
  border-radius: 50px;
  font-size: 0.875rem;
  font-weight: 500;
}

.pill-remove {
  background: none;
  border: none;
  color: #adb5bd;
  margin-left: 8px;
  font-size: 1.2rem;
  line-height: 1;
  cursor: pointer;
  padding: 0;
}

.pill-remove:hover {
  color: #dc3545;
}
</style>
